import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';

const api = axios.create({ baseURL: 'https://api.safecorepest.com/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const fmtMin = (min) => {
  if (min === null || min === undefined || isNaN(min)) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const varianceColor = (pct) => {
  if (pct === null || pct === undefined) return '#888';
  if (Math.abs(pct) <= 10) return '#16A34A';
  if (Math.abs(pct) <= 20) return '#D97706';
  return '#DC2626';
};

const varianceBg = (pct) => {
  if (pct === null || pct === undefined) return '#F9FAFB';
  if (Math.abs(pct) <= 10) return '#DCFCE7';
  if (Math.abs(pct) <= 20) return '#FEF3C7';
  return '#FEE2E2';
};

const Badge = ({ pct }) => {
  if (pct === null || pct === undefined) return <span style={{ color:'#ccc' }}>—</span>;
  return (
    <span style={{ backgroundColor: varianceBg(pct), color: varianceColor(pct), padding:'2px 8px', borderRadius:'10px', fontWeight:'700', fontSize:'11px', whiteSpace:'nowrap' }}>
      {pct > 0 ? '+' : ''}{pct}%
    </span>
  );
};

const KpiCard = ({ label, value, sub, color, icon }) => (
  <div style={{ backgroundColor:'#FAFAFA', borderRadius:'12px', padding:'16px', borderLeft:`4px solid ${color}`, minWidth:0 }}>
    <div style={{ fontSize:'11px', color:'#888', fontWeight:'600', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{icon} {label}</div>
    <div style={{ fontSize:'26px', fontWeight:'800', color }}>{value}</div>
    {sub && <div style={{ fontSize:'11px', color:'#999', marginTop:'3px' }}>{sub}</div>}
  </div>
);


// ── Export CSV ──────────────────────────────────────────────────────────────
function exportTimingCSV(data, filters) {
  if (!data) return;
  const wos = data.all_wos || [];
  if (!wos.length) { alert('No work order data to export.'); return; }

  const cols = [
    'order_number','scheduled_date','tech_name','facility_name','client_name',
    'service_type','status','scheduled_start','scheduled_end',
    'scheduled_min','actual_min','variance_min','efficiency_pct'
  ];
  const labels = [
    'WO #','Date','Technician','Facility','Client',
    'Service Type','Status','Sched Start','Sched End',
    'Sched Min','Actual Min','Variance Min','Efficiency %'
  ];
  const fmtVal = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') ? '"' + s.replace(/"/g,'""') + '"' : s;
  };
  const rows = wos.map(w => cols.map(c => fmtVal(w[c])).join(','));
  const csv = [labels.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = 'timing_performance_' + (filters.date_from||'') + '_' + (filters.date_to||new Date().toISOString().slice(0,10)) + '.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── Print ───────────────────────────────────────────────────────────────────
function printTiming(data, filters) {
  if (!data) return;
  const o   = data.overall || {};
  const wos = data.all_wos || [];
  const techs = data.technicians || [];
  const fmtN = (n) => n != null ? parseFloat(n).toFixed(1) : '—';

  const techRows = techs.map((t,i) => `
    <tr class="${i%2===0?'even':'odd'}">
      <td style="font-weight:700">${t.tech_name}</td>
      <td class="num">${t.total_wos}</td>
      <td class="num">${t.completed}</td>
      <td class="num">${fmtN(t.avg_scheduled_min)} min</td>
      <td class="num">${fmtN(t.avg_actual_min)} min</td>
      <td class="num" style="color:${parseFloat(t.avg_variance_min)>0?'#c62828':'#2e7d32'};font-weight:600">${fmtN(t.avg_variance_min)} min</td>
      <td class="num">${fmtN(t.avg_efficiency_pct)}%</td>
      <td class="num">${t.with_timing}</td>
    </tr>`).join('');

  const woRows = wos.slice(0,200).map((w,i) => `
    <tr class="${i%2===0?'even':'odd'}">
      <td style="font-weight:600">${w.order_number||'—'}</td>
      <td>${w.scheduled_date||'—'}</td>
      <td>${w.tech_name||'—'}</td>
      <td style="white-space:normal;max-width:120px">${w.facility_name||'—'}</td>
      <td>${w.service_type||'—'}</td>
      <td>${w.status||'—'}</td>
      <td class="num">${fmtN(w.scheduled_min)} min</td>
      <td class="num">${fmtN(w.actual_min)} min</td>
      <td class="num" style="color:${parseFloat(w.variance_min)>15?'#c62828':parseFloat(w.variance_min)<-15?'#1565c0':'#2e7d32'};font-weight:600">${fmtN(w.variance_min)} min</td>
      <td class="num">${w.efficiency_pct!=null?fmtN(w.efficiency_pct)+'%':'—'}</td>
    </tr>`).join('');

  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head>
    <title>Timing & Performance Report</title>
    <style>
      @page { size: landscape; margin: 12mm 10mm; }
      * { box-sizing:border-box; }
      body { font-family:Arial,sans-serif; font-size:9px; color:#222; margin:0; }
      .header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:10px; border-bottom:2px solid #2D5C3F; padding-bottom:6px; }
      .header h2 { color:#2D5C3F; margin:0; font-size:13px; }
      .header .meta { color:#666; font-size:8px; text-align:right; }
      .kpis { display:flex; gap:10px; margin-bottom:12px; }
      .kpi { flex:1; border:1px solid #ddd; border-radius:4px; padding:5px 8px; text-align:center; }
      .kpi .val { font-size:12px; font-weight:700; color:#2D5C3F; }
      .kpi .lbl { font-size:7px; color:#888; margin-top:1px; }
      h3 { color:#2D5C3F; font-size:10px; margin:14px 0 5px; border-bottom:1px solid #D4AF37; padding-bottom:3px; }
      table { width:100%; border-collapse:collapse; margin-bottom:8px; }
      th { background:#2D5C3F; color:#fff; padding:4px 5px; text-align:left; font-size:7.5px; white-space:nowrap; }
      th.num { text-align:right; }
      td { padding:3px 5px; border-bottom:1px solid #eee; font-size:7.5px; vertical-align:top; }
      td.num { text-align:right; }
      tr.even td { background:#fff; }
      tr.odd  td { background:#f9fafb; }
      .footer { margin-top:8px; font-size:8px; color:#aaa; display:flex; justify-content:space-between; border-top:1px solid #eee; padding-top:4px; }
    </style>
  </head><body>
    <div class="header">
      <h2>⏱️ Timing & Performance Report</h2>
      <div class="meta">
        ${filters.date_from||'All dates'} ${filters.date_from&&filters.date_to?'→':''} ${filters.date_to||''}<br/>
        Generated: ${new Date().toLocaleString('en-US')}
      </div>
    </div>
    <div class="kpis">
      <div class="kpi"><div class="val">${o.total_wos||0}</div><div class="lbl">Total WOs</div></div>
      <div class="kpi"><div class="val">${o.total_completed||0}</div><div class="lbl">Completed</div></div>
      <div class="kpi"><div class="val">${fmtN(o.avg_scheduled_min)} min</div><div class="lbl">Avg Scheduled</div></div>
      <div class="kpi"><div class="val">${fmtN(o.avg_actual_min)} min</div><div class="lbl">Avg Actual</div></div>
      <div class="kpi"><div class="val">${fmtN(o.avg_variance_min)} min</div><div class="lbl">Avg Variance</div></div>
      <div class="kpi"><div class="val">${fmtN(o.avg_efficiency_pct)}%</div><div class="lbl">Efficiency</div></div>
      <div class="kpi"><div class="val">${o.with_timing||0}</div><div class="lbl">With Timing</div></div>
    </div>
    <h3>Technician Summary</h3>
    <table>
      <thead><tr>
        <th>Technician</th>
        <th class="num">Total WOs</th><th class="num">Completed</th>
        <th class="num">Avg Scheduled</th><th class="num">Avg Actual</th>
        <th class="num">Avg Variance</th><th class="num">Efficiency</th>
        <th class="num">With Timing</th>
      </tr></thead>
      <tbody>${techRows||'<tr><td colspan="8" style="text-align:center;color:#999">No technician data</td></tr>'}</tbody>
    </table>
    <h3>Work Order Detail ${wos.length>200?'(first 200 of '+wos.length+')':''}</h3>
    <table>
      <thead><tr>
        <th>WO #</th><th>Date</th><th>Technician</th><th>Facility</th>
        <th>Type</th><th>Status</th>
        <th class="num">Sched Min</th><th class="num">Actual Min</th>
        <th class="num">Variance</th><th class="num">Efficiency</th>
      </tr></thead>
      <tbody>${woRows||'<tr><td colspan="10" style="text-align:center;color:#999">No WO data</td></tr>'}</tbody>
    </table>
    <div class="footer">
      <span>SafeCore Pest Management</span>
      <span>Confidential — Internal Use Only</span>
    </div>
    <script>window.onload=function(){window.print();}<\/script>
  </body></html>`);
  win.document.close();
}

export default function TimingPerformance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [techList, setTechList] = useState([]);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', technician_id: '' });
  const [applied, setApplied] = useState({});
  const [sortField, setSortField] = useState('scheduled_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedTech, setSelectedTech] = useState(null);

  // Get first/last day of current month as defaults
  const today = new Date();
  const defaultFrom = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const defaultTo   = new Date(today.getFullYear(), today.getMonth()+1, 0).toISOString().split('T')[0];

  const load = useCallback(async (f = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.date_from) params.append('date_from', f.date_from);
      if (f.date_to)   params.append('date_to',   f.date_to);
      if (f.technician_id) params.append('technician_id', f.technician_id);
      const res = await api.get(`/service-orders/timing-stats?${params}`);
      setData(res.data.data);
      setTechList(res.data.data.technician_list || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = { date_from: defaultFrom, date_to: defaultTo, technician_id: '' };
    setFilters(init);
    setApplied(init);
    load(init);
  }, []);

  const applyFilters = () => { setApplied({...filters}); setSelectedTech(null); load(filters); };
  const resetFilters = () => {
    const init = { date_from: defaultFrom, date_to: defaultTo, technician_id: '' };
    setFilters(init); setApplied(init); setSelectedTech(null); load(init);
  };

  const sortedWos = (list) => {
    if (!list) return [];
    return [...list].sort((a,b) => {
      let va = a[sortField], vb = b[sortField];
      if (va === null) va = sortDir === 'asc' ? Infinity : -Infinity;
      if (vb === null) vb = sortDir === 'asc' ? Infinity : -Infinity;
      if (typeof va === 'string') return sortDir==='asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir==='asc' ? va-vb : vb-va;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d==='asc'?'desc':'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => handleSort(field)} style={{ padding:'8px 10px', textAlign:'left', color:'#555', fontWeight:'700', fontSize:'12px', cursor:'pointer', userSelect:'none', whiteSpace:'nowrap', backgroundColor:'#F3F4F6' }}>
      {children} {sortField===field ? (sortDir==='asc'?'↑':'↓') : <span style={{color:'#ccc'}}>↕</span>}
    </th>
  );

  const o = data?.overall;
  if (data && !o) return <div style={{padding:'40px',textAlign:'center',color:'#888'}}>Error loading data</div>;
  const displayWos = selectedTech
    ? (data?.all_wos || []).filter(w => String(w.tech_id) === String(selectedTech))
    : (data?.all_wos || []);

  const S = {
    page:   { padding:'28px 32px', backgroundColor:'#F8FAF8', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif' },
    card:   { backgroundColor:'#FFF', borderRadius:'14px', padding:'20px 24px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:'20px' },
    title:  { fontSize:'20px', fontWeight:'800', color:'#1A3A2A', margin:'0 0 4px' },
    sub:    { fontSize:'13px', color:'#888', margin:0 },
    label:  { fontSize:'12px', fontWeight:'600', color:'#444', marginBottom:'4px', display:'block' },
    input:  { padding:'8px 12px', border:'1.5px solid #E5E7EB', borderRadius:'8px', fontSize:'13px', width:'100%', boxSizing:'border-box', outline:'none' },
    select: { padding:'8px 12px', border:'1.5px solid #E5E7EB', borderRadius:'8px', fontSize:'13px', width:'100%', boxSizing:'border-box', backgroundColor:'#FFF', outline:'none' },
    btn:    { padding:'9px 18px', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'700', border:'none' },
  };

  return (
    <Layout>
    <div style={S.page}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px' }}>
        <div>
          <h1 style={S.title}>⏱️ Service Timing & Performance</h1>
          <p style={S.sub}>Actual vs estimated service times · Technician efficiency · Variance analysis</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ ...S.card, padding:'16px 24px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto auto', gap:'12px', alignItems:'flex-end' }}>
          <div>
            <label style={S.label}>Date From</label>
            <input type="date" style={S.input} value={filters.date_from} onChange={e => setFilters(f=>({...f,date_from:e.target.value}))} />
          </div>
          <div>
            <label style={S.label}>Date To</label>
            <input type="date" style={S.input} value={filters.date_to} onChange={e => setFilters(f=>({...f,date_to:e.target.value}))} />
          </div>
          <div>
            <label style={S.label}>Technician</label>
            <select style={S.select} value={filters.technician_id} onChange={e => setFilters(f=>({...f,technician_id:e.target.value}))}>
              <option value="">All Technicians</option>
              {techList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <button onClick={applyFilters} style={{ ...S.btn, backgroundColor:'#2D5C3F', color:'#FFF', alignSelf:'flex-end' }}>Apply</button>
          <button onClick={resetFilters} style={{ ...S.btn, backgroundColor:'#F3F4F6', color:'#555', border:'1px solid #E5E7EB', alignSelf:'flex-end' }}>Reset</button>
        </div>
        {(applied.date_from || applied.date_to || applied.technician_id) && (
          <div style={{ marginTop:'10px', fontSize:'12px', color:'#2D5C3F', fontWeight:'600' }}>
            🔍 Showing: {applied.date_from||'all'} → {applied.date_to||'all'}
            {applied.technician_id && techList.find(t=>String(t.id)===String(applied.technician_id))
              ? ` · ${techList.find(t=>String(t.id)===String(applied.technician_id)).name}` : ''}
          </div>
        )}
      </div>

      {/* Export buttons */}
      {data && !loading && (
        <div style={{ display:'flex', gap:'10px', marginBottom:'16px', justifyContent:'flex-end' }}>
          <button
            onClick={() => exportTimingCSV(data, applied)}
            style={{ padding:'7px 16px', background:'#1565c0', color:'#fff', border:'none', borderRadius:'6px', fontWeight:700, cursor:'pointer', fontSize:'13px' }}>
            ⬇ CSV
          </button>
          <button
            onClick={() => printTiming(data, applied)}
            style={{ padding:'7px 16px', background:'#4a4a4a', color:'#fff', border:'none', borderRadius:'6px', fontWeight:700, cursor:'pointer', fontSize:'13px' }}>
            🖨️ Print
          </button>
        </div>
      )}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#888' }}>Loading timing data...</div>
      ) : !data ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#888' }}>No data available</div>
      ) : (<>

        {/* KPI Row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'14px', marginBottom:'20px' }}>
          <KpiCard icon="✅" label="Completed WOs"    value={o.total_completed}  sub={`${o.with_timing} with timing`}         color="#2D5C3F" />
          <KpiCard icon="⏱" label="Avg Actual Time"  value={fmtMin(o.avg_actual_min)}   sub="real service duration"          color="#1D4ED8" />
          <KpiCard icon="📋" label="Avg Estimated"    value={fmtMin(o.avg_estimated_min)} sub="scheduled window"              color="#7C3AED" />
          <KpiCard icon="📊" label="Avg Variance"     value={o.variance_pct !== null ? `${o.variance_pct > 0?'+':''}${o.variance_pct}%` : '—'} sub={o.variance_pct > 0?'running over':o.variance_pct < 0?'running under':'on target'} color={varianceColor(o.variance_pct)} />
          <KpiCard icon="⚡" label="Fastest Service"  value={fmtMin(o.min_actual_min)}   sub="shortest completed WO"          color="#059669" />
          <KpiCard icon="🐢" label="Longest Service"  value={fmtMin(o.max_actual_min)}   sub="longest completed WO"           color="#D97706" />
          <KpiCard icon="⚠️" label="High Variance"    value={o.high_variance_count}       sub=">20% off estimate"             color={o.high_variance_count > 0 ? '#DC2626' : '#16A34A'} />
          <KpiCard icon="🔄" label="In Progress"      value={o.in_progress}               sub="currently active"              color="#F59E0B" />
        </div>

        {/* Technician Performance Table */}
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <h3 style={{ margin:0, fontSize:'16px', fontWeight:'800', color:'#1A3A2A' }}>👷 Performance by Technician</h3>
            {selectedTech && (
              <button onClick={() => setSelectedTech(null)} style={{ ...S.btn, backgroundColor:'#FEE2E2', color:'#DC2626', padding:'5px 12px', fontSize:'12px' }}>✕ Clear filter</button>
            )}
          </div>
          {data.technicians.length === 0
            ? <p style={{ color:'#999', fontSize:'13px' }}>No technician data for selected period</p>
            : <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                  <thead>
                    <tr>
                      {['Technician','WOs','Timed','Avg Real','Avg Est','Min','Max','Variance','⚠️ High Var'].map(h => (
                        <th key={h} style={{ padding:'9px 12px', textAlign: h==='Technician'?'left':'center', color:'#555', fontWeight:'700', fontSize:'12px', backgroundColor:'#F3F4F6', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.technicians.map((t,i) => (
                      <tr key={i}
                        onClick={() => setSelectedTech(selectedTech===t.id ? null : t.id)}
                        style={{ borderBottom:'1px solid #F0F0F0', cursor:'pointer', backgroundColor: selectedTech===t.id ? '#F0FDF4' : 'transparent' }}>
                        <td style={{ padding:'9px 12px', fontWeight:'700', color:'#2D5C3F' }}>{t.name}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center', color:'#444' }}>{t.completed}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center', color:'#888', fontSize:'12px' }}>{t.with_timing}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center', fontWeight:'700', color:'#1D4ED8' }}>{fmtMin(t.avg_actual_min)}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center', color:'#7C3AED' }}>{fmtMin(t.avg_estimated_min)}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center', color:'#059669', fontSize:'12px' }}>{fmtMin(t.min_actual_min)}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center', color:'#D97706', fontSize:'12px' }}>{fmtMin(t.max_actual_min)}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center' }}><Badge pct={t.variance_pct} /></td>
                        <td style={{ padding:'9px 12px', textAlign:'center' }}>
                          {t.high_variance > 0
                            ? <span style={{ backgroundColor:'#FEE2E2', color:'#DC2626', padding:'2px 8px', borderRadius:'8px', fontWeight:'700', fontSize:'12px' }}>{t.high_variance}</span>
                            : <span style={{ color:'#16A34A', fontSize:'12px' }}>✅ 0</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontSize:'11px', color:'#AAA', marginTop:'8px', marginBottom:0 }}>💡 Click a row to filter the work order table below by that technician</p>
              </div>
          }
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>

          {/* High Variance WOs */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 14px', fontSize:'16px', fontWeight:'800', color:'#DC2626' }}>⚠️ High Variance Work Orders</h3>
            {data.high_variance_wos.length === 0
              ? <div style={{ backgroundColor:'#DCFCE7', borderRadius:'10px', padding:'16px', textAlign:'center', color:'#16A34A', fontWeight:'700' }}>✅ All WOs within 20% of estimate</div>
              : data.high_variance_wos.map((wo,i) => (
                  <div key={i} style={{ backgroundColor: varianceBg(wo.variance_pct), borderRadius:'10px', padding:'10px 14px', marginBottom:'8px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontWeight:'800', color:'#111', fontSize:'13px' }}>{wo.order_number}</span>
                      <Badge pct={wo.variance_pct} />
                    </div>
                    <div style={{ fontSize:'12px', color:'#444', marginTop:'2px' }}>{wo.facility_name} <span style={{color:'#888'}}>· {wo.client_name}</span></div>
                    <div style={{ fontSize:'11px', color:'#666', marginTop:'3px' }}>
                      👤 {wo.tech_name} · 📅 {wo.scheduled_date}
                    </div>
                    <div style={{ fontSize:'11px', color:'#888', marginTop:'2px' }}>
                      🕐 {wo.start_time} → {wo.end_time} · Real: <strong>{fmtMin(wo.actual_min)}</strong> vs Est: <strong>{fmtMin(wo.estimated_min)}</strong>
                    </div>
                  </div>
                ))
            }
          </div>

          {/* No-timing WOs summary */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 14px', fontSize:'16px', fontWeight:'800', color:'#1A3A2A' }}>📡 Data Coverage</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'14px' }}>
              <div style={{ backgroundColor:'#DCFCE7', borderRadius:'10px', padding:'14px', textAlign:'center' }}>
                <div style={{ fontSize:'28px', fontWeight:'800', color:'#16A34A' }}>{o.with_timing}</div>
                <div style={{ fontSize:'12px', color:'#166534', fontWeight:'600' }}>With Timing Data</div>
              </div>
              <div style={{ backgroundColor:'#FEF3C7', borderRadius:'10px', padding:'14px', textAlign:'center' }}>
                <div style={{ fontSize:'28px', fontWeight:'800', color:'#D97706' }}>{o.total_completed - o.with_timing}</div>
                <div style={{ fontSize:'12px', color:'#92400E', fontWeight:'600' }}>Missing Timing</div>
              </div>
            </div>
            <div style={{ backgroundColor:'#EFF6FF', borderRadius:'10px', padding:'14px', fontSize:'12px', color:'#1D4ED8' }}>
              <strong>Coverage:</strong> {o.total_completed > 0 ? Math.round(o.with_timing/o.total_completed*100) : 0}% of completed WOs have real timing data.
              <br/><br/>
              💡 Timing is captured automatically when technicians use Start / Complete in the mobile app. WOs completed before this feature was enabled will show as missing.
            </div>
          </div>
        </div>

        {/* All WOs Detail Table */}
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <h3 style={{ margin:0, fontSize:'16px', fontWeight:'800', color:'#1A3A2A' }}>
              📋 Work Order Detail
              {selectedTech && techList.find(t=>String(t.id)===String(selectedTech))
                ? ` — ${techList.find(t=>String(t.id)===String(selectedTech)).name}` : ''}
            </h3>
            <span style={{ fontSize:'12px', color:'#888' }}>{displayWos.length} records</span>
          </div>
          {displayWos.length === 0
            ? <p style={{ color:'#999', fontSize:'13px' }}>No work orders with timing data for selected filters</p>
            : <div style={{ overflowX:'auto', maxHeight:'420px', overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                  <thead style={{ position:'sticky', top:0, zIndex:1 }}>
                    <tr>
                      <SortTh field="order_number">WO #</SortTh>
                      <SortTh field="scheduled_date">Date</SortTh>
                      <SortTh field="tech_name">Technician</SortTh>
                      <SortTh field="facility_name">Facility</SortTh>
                      <SortTh field="start_time">Start</SortTh>
                      <SortTh field="end_time">End</SortTh>
                      <SortTh field="actual_min">Real Time</SortTh>
                      <SortTh field="estimated_min">Estimated</SortTh>
                      <SortTh field="variance_pct">Variance</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedWos(displayWos).map((wo,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid #F5F5F5', backgroundColor: i%2===0?'#FFF':'#FAFAFA' }}>
                        <td style={{ padding:'7px 10px', fontWeight:'700', color:'#2D5C3F' }}>{wo.order_number}</td>
                        <td style={{ padding:'7px 10px', color:'#555' }}>{wo.scheduled_date}</td>
                        <td style={{ padding:'7px 10px', color:'#444' }}>{wo.tech_name}</td>
                        <td style={{ padding:'7px 10px', color:'#444', maxWidth:'160px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{wo.facility_name}</td>
                        <td style={{ padding:'7px 10px', color:'#666', whiteSpace:'nowrap' }}>{wo.start_time||'—'}</td>
                        <td style={{ padding:'7px 10px', color:'#666', whiteSpace:'nowrap' }}>{wo.end_time||'—'}</td>
                        <td style={{ padding:'7px 10px', fontWeight:'700', color:'#1D4ED8', textAlign:'center' }}>{fmtMin(wo.actual_min)}</td>
                        <td style={{ padding:'7px 10px', color:'#7C3AED', textAlign:'center' }}>{fmtMin(wo.estimated_min)}</td>
                        <td style={{ padding:'7px 10px', textAlign:'center' }}><Badge pct={wo.variance_pct} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </div>

        {o.with_timing === 0 && (
          <div style={{ ...S.card, backgroundColor:'#EFF6FF', border:'1px solid #BFDBFE', textAlign:'center', padding:'24px' }}>
            <div style={{ fontSize:'32px', marginBottom:'8px' }}>📡</div>
            <div style={{ fontWeight:'700', color:'#1D4ED8', marginBottom:'6px' }}>Collecting Data</div>
            <div style={{ fontSize:'13px', color:'#3B82F6' }}>Timing data will appear here after technicians complete work orders via the mobile app.</div>
          </div>
        )}

      </>)}
    </div>
    </Layout>
  );
}
